import { useState, useEffect } from "react";

import {
  AspectRatio,
  Flex,
  Button,
  Center,
  Container,
  FileButton,
  Group,
  Modal,
  Text,
  Stack,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import Cropper, { Area, Point } from "react-easy-crop";
import { compressImage, getCroppedImg } from "./utils";
import { useAppTranslation } from "../../hooks/useTranslation";

/**
 * Where the picture handed to onChange came from: a crop of `defaultValue`
 * (the image already shown), or a file she brought in with Upload image or
 * Paste image. The desk counts only the second as an image edit (DESK-07,
 * review AD-CR-03); a plain re-crop of the same image is not one.
 */
export type EditPictureOrigin = "crop" | "upload";

type EditPictureProps = {
  defaultValue?: string | null;
  label?: string;
  width: number;
  height: number;
  onChange: (file: File, origin: EditPictureOrigin) => void;
  isError?: boolean;
  isApplicationEdit?: boolean;
  /**
   * Where the cropper should open. Optional and deliberately undefined-able:
   * `react-easy-crop` ignores an undefined initial area and frames the image
   * the way it always has, so leaving this out must be byte-for-byte today's
   * behaviour. Never substitute a zero rectangle for it — that is a nonsense
   * area, not an absent one.
   */
  initialCroppedAreaPixels?: Area;
};

export const EditPicture = (props: EditPictureProps) => {
  const {
    defaultValue,
    width,
    height,
    label,
    isError = false,
    onChange,
    isApplicationEdit = false,
    initialCroppedAreaPixels,
  } = props;
  const [preCroppedFile, setPreCroppedFile] = useState<File | null>(null);
  // Travels with preCroppedFile: what the cropper is cropping.
  const [preCroppedOrigin, setPreCroppedOrigin] =
    useState<EditPictureOrigin>("crop");
  const [file, setFile] = useState<File>();
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [showCrop, setShowCrop] = useState(false);
  const [cropHappening, setCropHappening] = useState(false);
  const [croppedArea, setCroppedArea] = useState<Area>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [imageWeight, setImageWeight] = useState<number>(0);
  const { t } = useAppTranslation();

  useEffect(() => {
    if (showCrop && defaultValue && !preCroppedFile) {
      fetch(defaultValue)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "image.jpg", { type: blob.type });
          setPreCroppedFile(file);
          setPreCroppedOrigin("crop");
        })
        .catch((err) => console.error("Error fetching the image:", err));
    }
  }, [showCrop, defaultValue, preCroppedFile]);

  const pasteImg = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      if (
        !clipboardItems.length ||
        !clipboardItems[0].types.includes("image/png")
      ) {
        notifications.show({
          title: t("images.paste_not_possible"),
          message: t("images.clipboard_no_images"),
          autoClose: 3000,
        });
      }
      const blobOutput = await clipboardItems[0].getType("image/png");
      const file = new File([blobOutput], "pasted-image.png", {
        type: "image/png",
      });
      setPreCroppedFile(file);
      setPreCroppedOrigin("upload");
      setShowCrop(true);
    } catch (e) {
      console.log(e);
    }
  };

  const cropPicture = async () => {
    setCropHappening(true);
    const croppedFile = await getCroppedImg(preCroppedFile!, croppedArea);
    // 0.5 MB = 500 KB. browser-image-compression reads maxSizeMB in megabytes,
    // so this must be 0.5, not 500 (which would be a 500 MB cap that never triggers).
    const compressedFile = await compressImage(croppedFile, 0.5);
    setFile(compressedFile);
    onChange(compressedFile, preCroppedOrigin);
    setShowCrop(false);
    setCropHappening(false);
  };

  // Application edit mode: show crop button if defaultValue exists and no file selected
  if (isApplicationEdit && defaultValue && !file) {
    return (
      <Container mt="md">
        {label && (
          <Center>
            <Text>{label}</Text>
          </Center>
        )}
        <AspectRatio ratio={width / height} maw={width} mx="auto">
          <img
            src={defaultValue || ""}
            alt=""
            style={{ border: "5px solid orange", borderRadius: 10 }}
            onLoad={(event) => {
              const img = event.target as HTMLImageElement;
              fetch(img.src)
                .then((res) => res.blob())
                .then((blob) => {
                  setImageWeight(Math.round(blob.size / 1024));
                });
            }}
          />
        </AspectRatio>
        <Center>
          <Stack align="center" mt="md">
            <Button onClick={() => setShowCrop(true)}>
              {t("images.crop_image")}
            </Button>
            <FileButton
              onChange={(newFile) => {
                if (newFile === null) return;
                setPreCroppedFile(newFile);
                setPreCroppedOrigin("upload");
                setShowCrop(true);
              }}
              accept="image/png,image/jpeg"
            >
              {(props) => (
                <Button {...props}>{t("images.upload_image")}</Button>
              )}
            </FileButton>
            <Button onClick={pasteImg}>{t("images.paste_image")}</Button>
          </Stack>
        </Center>
        {showCrop && (
          <Modal
            opened={showCrop}
            centered
            withCloseButton={false}
            onClose={() => setShowCrop(false)}
          >
            <Flex
              h={height + 100}
              direction="column"
              justify="flex-end"
              align="flex-end"
            >
              <Cropper
                image={
                  preCroppedFile ? URL.createObjectURL(preCroppedFile!) : ""
                }
                // No coordinate conversion, and none is missing. This prop is
                // in SOURCE-IMAGE pixels; the source image here is the captured
                // screenshot exactly as uploaded (the extension stopped
                // cropping), and `artwork_rect` was measured in screenshot
                // pixels by `computeCropGeometry`. The two spaces coincide.
                initialCroppedAreaPixels={initialCroppedAreaPixels}
                crop={crop}
                zoom={zoom}
                aspect={width / height}
                onCropChange={setCrop}
                onCropComplete={(
                  croppedArea: Area,
                  croppedAreaPixels: Area
                ) => {
                  setCroppedArea(croppedAreaPixels);
                }}
                onZoomChange={setZoom}
              />
              <Group>
                <Button color="gray" onClick={() => setShowCrop(false)}>
                  {t("images.cancel")}
                </Button>
                <Button onClick={cropPicture} loading={cropHappening}>
                  {t("images.crop")}
                </Button>
              </Group>
            </Flex>
          </Modal>
        )}
      </Container>
    );
  }

  // Regular mode
  return (
    <>
      <Center>{label && <Text>{label}</Text>}</Center>
      {file || defaultValue ? (
        <AspectRatio ratio={width / height} maw={width} mx="auto">
          <img
            src={file ? URL.createObjectURL(file) : defaultValue || ""}
            alt=""
            onLoad={(event) => {
              const img = event.target as HTMLImageElement;
              fetch(img.src)
                .then((res) => res.blob())
                .then((blob) => {
                  setImageWeight(Math.round(blob.size / 1024));
                });
            }}
          />
        </AspectRatio>
      ) : (
        <Container
          mt="md"
          style={{
            height,
            width,
            border: `1px solid ${isError ? "red" : "grey"}`,
            borderRadius: 10,
          }}
        />
      )}
      <Center>
        <Stack align="center">
          <Stack mt={imageWeight > 0 ? "sm" : "lg"} align="center">
            <FileButton
              onChange={(newFile) => {
                if (newFile === null) return;
                setPreCroppedFile(newFile);
                setPreCroppedOrigin("upload");
                setShowCrop(true);
              }}
              accept="image/png,image/jpeg"
            >
              {(props) => (
                <Button {...props}>{t("images.upload_image")}</Button>
              )}
            </FileButton>
            <Button onClick={pasteImg}>{t("images.paste_image")}</Button>
          </Stack>
        </Stack>
      </Center>
      <Modal
        opened={showCrop}
        centered
        withCloseButton={false}
        onClose={() => {
          setShowCrop(false);
        }}
      >
        <Flex
          h={height + 100}
          direction="column"
          justify="flex-end"
          align="flex-end"
        >
          <Cropper
            image={preCroppedFile ? URL.createObjectURL(preCroppedFile!) : ""}
            // Same seeding as the application-edit modal above, and for the
            // same reason: re-cropping the same screenshot must open in the
            // same place. Passing it to only one of the two Croppers is the
            // bug that reads as "it works for imports but not when I re-crop".
            initialCroppedAreaPixels={initialCroppedAreaPixels}
            crop={crop}
            zoom={zoom}
            aspect={width / height}
            onCropChange={setCrop}
            onCropComplete={(croppedArea: Area, croppedAreaPixels: Area) => {
              setCroppedArea(croppedAreaPixels);
            }}
            onZoomChange={setZoom}
          />
          <Group>
            <Button
              color="gray"
              onClick={() => {
                setShowCrop(false);
              }}
            >
              {t("images.cancel")}
            </Button>
            <Button onClick={cropPicture} loading={cropHappening}>
              {t("images.crop")}
            </Button>
          </Group>
        </Flex>
      </Modal>
    </>
  );
};
